import React, {useEffect, useMemo} from 'react';
import {TARating} from '@stencil/gi-components/src';

import {
  reviewSummaryGISelector,
  isAltAccoSelector,
} from '@stencil/hotels-cg-reducer/src/details/selectors';
import {
  countrySelectorFn,
  pokusExpDataSelectorFn,
  requestQuerySelectorFn,
} from '@stencil/hotels-common-reducer/src/selectors';
import {ugcSummarySelector} from '@stencil/hotels-cg-reducer/src/ugc/selectors';
import {detailsNavigationIdsAndValues} from '@stencil/hotels-utils/src/get-details-navigation-id-and-values';
import {scrollToEl} from '@stencil/hotels-utils/src/scroll-into-view-details';

// HOOKS
import {useSelector, useDispatch} from 'react-redux';
// ICONS
import TriggerAnchorText from '../../../../../components/TriggerActionText';
import {getRatingsColor} from '@stencil/hotels-utils/src/get-ratings-colors';
// UTILS
import {ratingColorSelector} from '../../../../../utils/UI';
import {isValidArray} from '@stencil/hotels-utils/src/common-utils';
import {
  getHotelRatingBucket,
  ratingsReviewSrcAdaptor,
} from '@stencil/hotels-utils/src/ratings-review-utils';
import {FlexItem} from '../../../../../../stories/components/CommonStyleUtils/dwebCommon.styles';
import {omnitureThunk, omnitureConfig} from '@stencil/omniture-analytics/src';
import {
  DetailsPageComponents,
  detailsClick,
} from '@stencil/omniture-analytics/src/constants/details';
import {omnitureUtils} from '@stencil/omniture-analytics';

// STYLES
import * as S from './ReviewInfo.styles';

interface AggregateRatingProps {
  isNewListing: boolean;
  isExternalRating: boolean;
  giDataExists?: boolean;
  isDwebVideo?: boolean;
  country?: string;
  hotelRating: number;
  ratingCount?: number;
  rnr?: Array<any>;
}

const AggregateRatingAndNumberOfRatings: React.FC<AggregateRatingProps> = ({
  isNewListing,
  isDwebVideo = false,
  hotelRating,
  rnr = [],
}) => {
  const ratingBlockRenderer = (hotelRating: number, isNewListing: boolean = false) => {
    return isNewListing ? (
      <S.NewWrapper bgColor={getRatingsColor(2.8)}>
        <S.New>New</S.New>
        <span>New Listing</span>
      </S.NewWrapper>
    ) : (
      <S.ReviewOuterWrapper>
        <S.ReviewInnerWrapper>
          <S.RatingWrapper bgColor={getRatingsColor(Number(hotelRating))}>
            <S.ReviewNumber color={ratingColorSelector(Number(hotelRating))}>
              <span itemProp="worstRating" content="1" />
              <span itemProp="ratingValue">{hotelRating}</span>
              <span>/</span>
              <S.OutOfNumberForReview itemProp="bestRating">5</S.OutOfNumberForReview>
            </S.ReviewNumber>
          </S.RatingWrapper>
          <S.NewViewMapCTA>{isValidArray(rnr) ? 'View Reviews' : 'Guest Ratings'}</S.NewViewMapCTA>
        </S.ReviewInnerWrapper>
      </S.ReviewOuterWrapper>
    );
  };

  if (isNewListing) {
    return ratingBlockRenderer(2.8, true);
  }
  if (!!hotelRating) {
    return ratingBlockRenderer(hotelRating);
  }
  return null;
};

function ReviewInfo({isSeo = false}) {
  const dispatch = useDispatch();

  const reviewSummaryGI = useSelector(reviewSummaryGISelector);
  const isAltAcco = useSelector(isAltAccoSelector);
  const country = useSelector(countrySelectorFn);
  const ugcSummary = useSelector(ugcSummarySelector);
  const pokusExpData = useSelector(pokusExpDataSelectorFn);
  const requestQuery = useSelector(requestQuerySelectorFn);

  const {reviewData, isExternalRating, isNewListing, isTripAdvisor, isGIData, isExpData} =
    ratingsReviewSrcAdaptor(reviewSummaryGI, country);

  useEffect(() => {
    if (reviewData?.hotelRating) {
      omnitureUtils.setLoadedComponents(
        `rating_` +
          getHotelRatingBucket(reviewData?.hotelRating) +
          '|' +
          reviewSummaryGI?.ugcFunnelReviewSrc,
      );
    }
  }, [reviewData]);

  const {hotelRating, ratingCount, availableOTAs} = useMemo(() => {
    return {
      hotelRating: ugcSummary?.cumulativeRating,
      ratingCount: ugcSummary?.ratingCount,
      availableOTAs: ugcSummary?.availableOTAs,
    };
  }, [reviewData, ugcSummary]);

  if (!reviewSummaryGI) {
    return null;
  }

  const onAnchorClickHandler = e => {
    e.preventDefault();
    const reviewTypeText = isExpData ? 'EXP' : 'GI';
    dispatch(
      omnitureThunk(omnitureConfig.pages.DetailsUnif, omnitureConfig.events.ctaClick, {
        cta: {
          name: `${DetailsPageComponents.ReviewInfo}_${
            isTripAdvisor
              ? `${detailsClick.reviewInfo.topTAReviewClick}|TAR_${reviewData?.hotelRating}|TARC_${reviewData?.ratings?.length}|TARV_${reviewData?.reviewCount}`
              : `${
                  isExpData
                    ? detailsClick.reviewInfo.topEXPReviewClick
                    : detailsClick.reviewInfo.topGIReviewClick
                }|${reviewTypeText}R_${reviewData?.hotelRating}|${reviewTypeText}RC_${
                  reviewData?.ratingsOnlyCount
                }|${reviewTypeText}RV_${reviewData?.reviewCount}`
          }`,
          type: omnitureConfig.ctaType.Button,
          componentName: DetailsPageComponents.ReviewInfo,
          destination: '',
        },
      }),
    );
    scrollToEl(detailsNavigationIdsAndValues.guestReviews.id);
  };

  const onMapClickHandler = e => {
    e.stopPropagation();
    dispatch(
      omnitureThunk(omnitureConfig.pages.DetailsUnif, omnitureConfig.events.ctaClick, {
        cta: {
          name: `${DetailsPageComponents.ReviewInfo}_${detailsClick.reviewInfo.viewMapClick}`,
          type: omnitureConfig.ctaType.Button,
          componentName: DetailsPageComponents.ReviewInfo,
          destination: '',
        },
      }),
    );
    scrollToEl(detailsNavigationIdsAndValues.location.id);
  };

  const renderChild = () => {
    return isTripAdvisor ? (
      <S.OuterWrapper>
        <S.TARatingText>Tripadvisor Traveler Rating</S.TARatingText>
        <S.TARatingContainer>
          <TARating rating={reviewData?.hotelRating} />
          {reviewData?.reviewCount && (
            <S.TARatingWithCountText>
              {reviewData?.reviewCount} {'Ratings'}
            </S.TARatingWithCountText>
          )}
        </S.TARatingContainer>
      </S.OuterWrapper>
    ) : (
      <AggregateRatingAndNumberOfRatings
        isNewListing={isNewListing}
        isExternalRating={isExternalRating}
        hotelRating={hotelRating}
        isDwebVideo={!isAltAcco}
        rnr={availableOTAs}
      />
    );
  };

  return (
    <TriggerAnchorText onClick={onAnchorClickHandler}>
      <S.FlexItemColumn>
        <FlexItem>
          <S.RatingOuterWrapper onClick={onMapClickHandler}>
            <S.ImageWrapper>
              <S.NewViewMap>
                <S.NewMapPin src="https://gos3.ibcdn.com/mapPin-1638795585.png" />
              </S.NewViewMap>
              <S.NewViewMapCTA>View Map</S.NewViewMapCTA>
            </S.ImageWrapper>
          </S.RatingOuterWrapper>
          {renderChild()}
        </FlexItem>
      </S.FlexItemColumn>
    </TriggerAnchorText>
  );
}

export default ReviewInfo;
